import { BiSolidUserAccount } from "solid-icons/bi";
import { IoPersonOutline } from "solid-icons/io";
import { For, Show } from "solid-js";
import { useI18n } from "@/api/i18n";
import { AvatarToDeprecate } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import type { FreelancerProfile } from "@/domains/company/active-search/types";

type SelectedFreelancer = Pick<
  FreelancerProfile,
  "id" | "full_name" | "avatar_url" | "job_title" | "already_applied"
>;

type FreelancePreviewCardProps = {
  freelancers: SelectedFreelancer[];
};

export function FreelancePreviewCard(props: FreelancePreviewCardProps) {
  const { t } = useI18n();

  return (
    <Card class="bg-white text-left">
      <CardHeader class="space-y-1">
        <div class="flex items-start gap-2">
          <IoPersonOutline class="w-5 h-5 text-gray-700 mt-0.5 shrink-0" />
          <div class="min-w-0">
            <CardTitle class="text-base">
              {t(
                "company.search.freelancers.send_proposal.preview.freelancer.title",
              )}
            </CardTitle>
            <CardDescription>
              {t(
                "company.search.freelancers.send_proposal.preview.freelancer.description",
              )}
            </CardDescription>
          </div>
        </div>
      </CardHeader>

      <CardContent class="space-y-3">
        <For each={props.freelancers}>
          {(freelancer) => (
            <div class="flex items-center justify-between gap-3 rounded-lg border border-gray-100 p-3">
              <div class="flex items-center gap-3 min-w-0">
                <AvatarToDeprecate
                  src={freelancer.avatar_url ?? ""}
                  alt={freelancer.full_name ?? ""}
                  class="h-12 w-12 shrink-0"
                  fallback={<BiSolidUserAccount class="w-6 h-6" />}
                />
                <div class="min-w-0">
                  <p class="font-medium text-gray-900 truncate">
                    {freelancer.full_name || `#${freelancer.id}`}
                  </p>
                  <Show when={freelancer.job_title}>
                    <p class="text-sm text-gray-600 truncate">
                      {freelancer.job_title}
                    </p>
                  </Show>
                </div>
              </div>

              <Show when={freelancer.already_applied}>
                <Badge variant="secondary" class="shrink-0">
                  {t(
                    "company.search.freelancers.send_proposal.recap.already_applied_badge",
                  )}
                </Badge>
              </Show>
            </div>
          )}
        </For>
      </CardContent>
    </Card>
  );
}
